<template>
  <div
    class="py-4 container-fluid min-vh-100 user-page"
    :class="isDarkMode ? 'user-page-dark' : 'bg-light-moniex'"
    :style="isDarkMode
      ? 'background: #1E293B !important; min-height: 100vh;'
      : 'background: #f8f9fa; min-height: 100vh;'"
  >
    <div
      class="user-hero-banner mb-4"
      :style="isDarkMode
        ? 'background: #0F172A !important; border: 1px solid #334155 !important; box-shadow: none !important; color: #E2E8F0 !important; border-radius: 0.65rem !important;'
        : 'border-radius: 0.65rem !important;'"
    >
      <div class="user-hero-content d-flex flex-column flex-sm-row align-items-center gap-4">
        <img
          v-if="heroAvatarSrc"
          :src="heroAvatarSrc"
          class="user-hero-photo"
          :class="{ 'user-hero-photo-dark': isDarkMode }"
          :alt="userGames.UserDetail.UserNickName || 'Hráč'"
        >
        <div
          v-else
          class="user-hero-photo"
          :class="{ 'user-hero-photo-dark': isDarkMode }"
          aria-hidden="true"
        ></div>
        <div class="text-center text-sm-start">
          <h1
            :style="isDarkMode
              ? 'color: #FF8A4C !important; font-weight: 800 !important; font-size: 2rem !important; letter-spacing: -0.02em;'
              : 'font-weight: 800 !important; font-size: 2rem !important; letter-spacing: -0.02em;'"
          >{{ userGames.UserDetail.UserNickName || 'Hráč' }}</h1>
          <p class="mb-3" :style="isDarkMode ? 'color: #CBD5E1 !important; opacity: 1;' : ''">
            Veřejný přehled her — kam je přihlášený, co právě hraje a co už dohrál.
          </p>
          <div class="d-flex flex-wrap justify-content-center justify-content-sm-start gap-2">
            <span class="user-stat-chip" :class="isDarkMode ? 'user-stat-chip-dark' : ''">
              {{ userGames.WaitingGame.length }} čekající
            </span>
            <span class="user-stat-chip" :class="isDarkMode ? 'user-stat-chip-dark' : ''">
              {{ userGames.RunningGame.length }} běžící
            </span>
            <span class="user-stat-chip" :class="isDarkMode ? 'user-stat-chip-dark' : ''">
              {{ userGames.FinishedGame.length }} ukončené
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="dataLoaded" class="row align-items-start">
      <div class="col-12 col-lg-8 mb-4">
        <user-games-card
          :finished-game="userGames.FinishedGame"
          :running-game="userGames.RunningGame"
          :waiting-game="userGames.WaitingGame"
          :user-detail="userGames.UserDetail"
        />
      </div>
      <div class="col-12 col-lg-4 mb-4">
        <ActiveGamesSidebar />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import dataService from "@/services/dataService.js";
import { userAvatarUrl } from "@/helpers/userAvatars";
import UserGamesCard from "./components/custom/UserGamesCard.vue"
import ActiveGamesSidebar from "./components/custom/ActiveGamesSidebar.vue";

const route = useRoute();
const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const heroAvatarSrc = computed(() => userAvatarUrl(
  userGames.UserDetail.UserImage,
  store.state.imageCacheKey,
  dataLoaded.value
));

const dataLoaded = ref(false);
const userGames = reactive({
  FinishedGame: [],
  RunningGame: [],
  WaitingGame: [],
  UserDetail: {}
})

const userID = computed(() => route.params.uid);

const getUserGame = async (userId) => {
  if (!userId) return;
  dataLoaded.value = false;
  try {
    const response = await dataService.GetUserGame(userId);
    userGames.FinishedGame = response?.FinishedGame || [];
    userGames.RunningGame = response?.RunningGame || [];
    userGames.WaitingGame = response?.WaitingGame || [];
    userGames.UserDetail = response?.UserDetail || {};
    dataLoaded.value = true;
  }
  catch(error) {
    console.error('Failed to load user data', error)
  }
}

getUserGame(userID.value);

watch(userID, (id) => {
  getUserGame(id);
});
</script>

<style scoped>
.user-hero-banner {
  background: linear-gradient(135deg, #FFE8D6 0%, #FFD6BB 100%);
  border: 1px solid #FEC88F;
  border-radius: 0.65rem;
  padding: 32px 30px;
  color: #0F172A;
  box-shadow: none;
}

.user-hero-content h1 {
  font-family: 'Ubuntu Sans', Ubuntu, sans-serif;
  font-size: 2rem;
  font-weight: 800 !important;
  letter-spacing: -0.02em;
  line-height: 1.2;
  margin-bottom: 8px;
  color: #C2460F;
}

.user-hero-content p {
  opacity: 1;
  font-size: 1.05rem;
  color: #56647A;
}

.user-hero-photo {
  width: 112px;
  height: 112px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(255, 255, 255, 0.85);
  flex-shrink: 0;
  background: #ffffff;
}

.user-hero-photo-dark {
  border-color: #334155;
}

.user-stat-chip {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.user-stat-chip-dark {
  background: #1E293B;
  color: #E2E8F0;
  border: 1px solid #334155;
}

.bg-light-moniex {
  background-color: #f8f9fa;
}

.user-page-dark :deep(.card) {
  background: #0F172A !important;
  box-shadow: none !important;
  border: 1px solid #334155 !important;
  color: #E2E8F0 !important;
}

.user-page-dark :deep(.card-header),
.user-page-dark :deep(.card-body) {
  background: transparent !important;
  color: #E2E8F0 !important;
}

.user-page-dark :deep(h5),
.user-page-dark :deep(h6),
.user-page-dark :deep(.text-dark) {
  color: #E2E8F0 !important;
}

.user-page-dark :deep(.vgt-inner-wrap),
.user-page-dark :deep(.vgt-responsive),
.user-page-dark :deep(.vgt-wrap) {
  background: transparent !important;
  box-shadow: none !important;
}

.user-page-dark :deep(.vgt-table) {
  background: transparent !important;
  color: #E2E8F0 !important;
  border: none !important;
}

.user-page-dark :deep(.vgt-table th) {
  background: #1E293B !important;
  color: #94A3B8 !important;
  border-color: #334155 !important;
}

.user-page-dark :deep(.vgt-table td) {
  background: transparent !important;
  color: #E2E8F0 !important;
  border-color: #334155 !important;
}

.user-page-dark :deep(.vgt-table tbody tr:hover),
.user-page-dark :deep(.vgt-table tr.clickable:hover) {
  background-color: #1E293B !important;
}

.user-page-dark :deep(.vgt-wrap__footer) {
  background: #1E293B !important;
  color: #94A3B8 !important;
  border: none !important;
}

.user-page-dark :deep(.vgt-table th.sortable button),
.user-page-dark :deep(.vgt-table th.sortable span) {
  color: #94A3B8 !important;
}
</style>
